/**
 * Loads Calendly's popup widget and opens the scheduler whenever a
 * `#contact-sales` CTA is clicked.
 *
 * Mirrors the previous Qualified integration: the "Contact sales" /
 * "Product specialist chat" CTAs across the app only carry `id="contact-sales"`
 * (and fire their own click analytics). The scheduler itself is opened here
 * through a single delegated click handler, so every current and future CTA
 * keeps working without wiring Calendly into each one.
 *
 * Analytics follow the internal Calendly embed guide: on a completed booking
 * (`calendly.event_scheduled`) we fire the governed `meeting_booked` event.
 * Because the app is authenticated, the signed-in user's email/name are passed
 * as prefill so the booking (and the downstream Salesforce lead) match the
 * known user.
 */

import { calendlySchedulingUrl } from '@config';

export interface SchedulerUser {
  email?: string;
  name?: string;
}

// RudderStack sales scheduling link, set via REACT_APP_CALENDLY_SCHEDULING_URL (no default).
const CALENDLY_SCHEDULING_URL = calendlySchedulingUrl;

// Official Calendly embed assets — see https://calendly.com/help/embed-options-overview
const CALENDLY_ASSETS_ORIGIN = 'https://assets.calendly.com';
const CALENDLY_WIDGET_CSS = `${CALENDLY_ASSETS_ORIGIN}/assets/external/widget.css`;
const CALENDLY_WIDGET_JS = `${CALENDLY_ASSETS_ORIGIN}/assets/external/widget.js`;

// Calendly's popup iframe posts booking lifecycle messages from this origin.
const CALENDLY_POPUP_ORIGIN = 'https://calendly.com';

const CONTACT_SALES_SELECTOR = '#contact-sales';

// Signed-in user details, used to prefill the scheduler. Refreshed on each
// `loadCalendlyScheduler` call (e.g. re-identify) before the load guard.
let schedulerUser: SchedulerUser = {};

const preconnect = (href: string) => {
  if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
    return;
  }
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = href;
  document.head.appendChild(link);
};

const injectWidgetAssets = () => {
  // Warm up DNS/TLS to both Calendly hosts before the popup is opened.
  preconnect(CALENDLY_ASSETS_ORIGIN);
  preconnect(CALENDLY_POPUP_ORIGIN);

  if (!document.querySelector(`link[href="${CALENDLY_WIDGET_CSS}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = CALENDLY_WIDGET_CSS;
    document.head.appendChild(link);
  }

  if (!document.querySelector(`script[src="${CALENDLY_WIDGET_JS}"]`)) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = CALENDLY_WIDGET_JS;
    script.async = true;
    document.head.appendChild(script);
  }
};

const buildPrefill = (): SchedulerUser => {
  const { email, name } = schedulerUser;
  const prefill: SchedulerUser = {};
  if (email) {
    prefill.email = email;
  }
  if (name) {
    prefill.name = name;
  }
  return prefill;
};

// Open the scheduler for any click that lands on (or inside) a `#contact-sales`
// element. Delegation keeps every CTA working through a single listener.
const handleContactSalesClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement | null;
  if (!target?.closest(CONTACT_SALES_SELECTOR) || !CALENDLY_SCHEDULING_URL) {
    return;
  }

  // Stop links/buttons from navigating or submitting before the modal opens.
  event.preventDefault();

  if (window.Calendly) {
    window.Calendly.initPopupWidget({ url: CALENDLY_SCHEDULING_URL, prefill: buildPrefill() });
  } else {
    // Widget script not ready (slow network / blocked) — fall back to a new tab.
    window.open(CALENDLY_SCHEDULING_URL, '_blank', 'noopener,noreferrer');
  }
};

const trackMeetingBooked = (properties: Record<string, unknown>) => {
  const analytics = (
    window as unknown as {
      rudderanalytics?: {
        track?: (event: string, properties?: Record<string, unknown>) => void;
      };
    }
  ).rudderanalytics;
  // TODO: register `meeting_booked` in the webapp tracking plan (coordinate the
  // name/properties with the data team) before enabling the flag in production.
  analytics?.track?.('meeting_booked', properties);
};

// Fire the governed `meeting_booked` event on a completed booking. Only trust
// messages from Calendly's origin (the popup runs in a cross-origin iframe).
const handleCalendlyMessage = (event: MessageEvent) => {
  if (event.origin !== CALENDLY_POPUP_ORIGIN) {
    return;
  }

  const data = event.data as
    | { event?: string; payload?: { event?: { uri?: string }; invitee?: { uri?: string } } }
    | undefined;

  if (data?.event !== 'calendly.event_scheduled') {
    return;
  }

  trackMeetingBooked({
    calendly_url: CALENDLY_SCHEDULING_URL,
    event_uri: data.payload?.event?.uri,
    invitee_uri: data.payload?.invitee?.uri,
    page_title: document.title,
    page_url: window.location.href,
  });
};

export const loadCalendlyScheduler = (user: SchedulerUser = {}) => {
  schedulerUser = user;

  if (window.__calendlySchedulerLoaded) {
    return;
  }
  window.__calendlySchedulerLoaded = true;

  injectWidgetAssets();
  document.addEventListener('click', handleContactSalesClick);
  window.addEventListener('message', handleCalendlyMessage);
};
